import { cn } from '@/lib/utils';
import { sponsorLogoMaxHeight, SPONSOR_TICKER_SECONDS_PER_LOGO } from '@/lib/sponsorLogo';

interface Props {
  /** Display-ordered logo URLs — resolveSponsorLogos() output. */
  logos: string[];
  /** Percent of the 40px base (50–200); null/undefined = 100. */
  size?: number | null;
  /** 'band' = primary-coloured footer strip (feature screens);
   *  'bare' = plain centred row (home view, poll sheet). */
  variant?: 'band' | 'bare';
  /** Band background — the club's brand colour. */
  primaryColor?: string;
  /** Extra wrapper classes (home passes its "mt-12 pb-4" spacing). */
  className?: string;
  /** Optional caption above the logos, e.g. t('common.officialPartners').
   *  The caller picks labelColor because only it knows the surface. */
  label?: string;
  labelColor?: string;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The club's sponsor logo(s). One logo renders statically; several drift
 * through the band as a continuously scrolling ticker (stadium-LED-board
 * style): the track holds the sequence twice and the CSS loop translates it
 * by -50% so the join is seamless, edges dissolve through a gradient mask
 * instead of hard-clipping, and duration scales with the logo count so the
 * drift speed is constant. Fans with reduced motion enabled get a static
 * wrapped row (the CSS media query also halts the animation as a second
 * guard).
 */
export function SponsorLogo({
  logos,
  size,
  variant = 'band',
  primaryColor,
  className,
  label,
  labelColor,
}: Props) {
  const count = logos.length;
  if (count === 0) return null;

  const maxHeight = sponsorLogoMaxHeight(size);
  const wrapperClass = cn(
    'flex flex-col items-center',
    variant === 'band' ? 'py-6' : '',
    className,
  );
  const wrapperStyle = variant === 'band' ? { backgroundColor: primaryColor } : undefined;

  const caption = label ? (
    <p
      className="text-[9px] font-bold uppercase tracking-[0.3em] text-center mb-2.5 opacity-70"
      style={{ color: labelColor }}
    >
      {label}
    </p>
  ) : null;

  if (count === 1) {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        {caption}
        <img
          src={logos[0]}
          alt="Sponsor"
          className="max-w-[80%] object-contain"
          style={{ maxHeight }}
        />
      </div>
    );
  }

  if (prefersReducedMotion()) {
    // Every sponsor still shows, just without motion — a wrapped row at a
    // reduced height so several logos share the width comfortably.
    const rowHeight = Math.max(12, Math.round(maxHeight * 0.6));
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        {caption}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 max-w-[86%]">
          {logos.map((url, i) => (
            <img
              key={`${i}-${url}`}
              src={url}
              alt="Sponsor"
              className="object-contain"
              style={{ maxHeight: rowHeight }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      {caption}
      {/* The viewport reserves the full band height so the layout never
          jumps; the track scrolls inside it. */}
      <div className="overflow-hidden w-full sponsor-ticker-mask" style={{ height: maxHeight }}>
        <div
          className="flex items-center gap-14 w-max h-full pr-14 animate-sponsor-marquee"
          style={{ animationDuration: `${count * SPONSOR_TICKER_SECONDS_PER_LOGO}s` }}
        >
          {[...logos, ...logos].map((url, i) => (
            <img
              key={`${i}-${url}`}
              src={url}
              alt="Sponsor"
              className="object-contain flex-shrink-0"
              style={{ maxHeight }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
